import * as SliderPrimitive from "@radix-ui/react-slider";
import * as React from "react";

import { cn } from "@gettactic/helpers";

export type SliderProps = React.ComponentPropsWithoutRef<
  typeof SliderPrimitive.Root
>;

/**
 * Picks a number (or a range, with two values) by dragging or with the arrow keys (Radix Slider).
 * `value` and `onValueChange` take arrays: `value={[hour]}`. Name it with `aria-label` or
 * `aria-labelledby`; they are forwarded to the thumb, which is the element screen readers focus.
 */
const Slider = React.forwardRef<
  React.ComponentRef<typeof SliderPrimitive.Root>,
  SliderProps
>(
  (
    {
      className,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      ...props
    },
    ref
  ) => (
    <div className="relative flex items-center">
      <SliderPrimitive.Root
        ref={ref}
        className={cn(
          "flex h-1 w-full touch-none select-none items-center rounded-full",
          className
        )}
        orientation="horizontal"
        {...props}
      >
        <SliderPrimitive.Track className="relative h-1 grow overflow-hidden rounded-full bg-neutral-1000">
          <SliderPrimitive.Range className="" />
        </SliderPrimitive.Track>
        {/* The thumb has the slider role, so it is the element that needs the name */}
        <SliderPrimitive.Thumb
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          className="block h-4 w-4 cursor-pointer rounded-full border-1 border-neutral-400 bg-background ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
        />
      </SliderPrimitive.Root>
    </div>
  )
);
Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };
